import { useEffect, useState } from 'react';

const IOS_REGEX = (/iPhone|iPad|iPod/);
const MOBILE_REGEX = new RegExp(
  `Android|webOS|${IOS_REGEX.source}|BlackBerry|IEMobile|Opera Mini`, 'i'
);
// WebView in-app Android : l'installation PWA n'y existe pas
// (BeforeInstallPromptEvent absent)
const WEBVIEW_REGEX = (/; wv\)|GSA\/|FB_IAB|FBAN|FBAV|Instagram|Line\//i);
// Navigateurs Android autres que Chrome : soit ils ne posent qu'un
// raccourci, soit (Samsung Internet, Mi Browser...) ils fabriquent
// eux-memes un APK dont le targetSdk est trop ancien, que Play Protect
// bloque ("Appli non securisee bloquee"). Dans tous ces cas on renvoie
// vers Chrome, seul a produire un WebAPK signe par Google.
const CHROME_REGEX = (/Chrome\/\d/);
const OTHER_BROWSER_REGEX = new RegExp(
  'SamsungBrowser|MiuiBrowser|HuaweiBrowser|HeyTapBrowser|OppoBrowser'
  + '|VivoBrowser|YaBrowser|EdgA|OPR/|OPT/|Whale|UCBrowser|DuckDuckGo',
  'i'
);

export const isMobileDevice = MOBILE_REGEX.test(navigator.userAgent);
export const isIOS = IOS_REGEX.test(navigator.userAgent);
const isAndroid = (/Android/i).test(navigator.userAgent);

// True des que l'installation doit passer par Chrome plutot que par le
// prompt natif du navigateur courant.
export const needsChrome = WEBVIEW_REGEX.test(navigator.userAgent)
  || (isAndroid && (
    OTHER_BROWSER_REGEX.test(navigator.userAgent)
    || !CHROME_REGEX.test(navigator.userAgent)
  ));

export const isInApp =
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const INSTALL_STORAGE_KEY = 'pwa_install_status';
const INSTALLED_STATUS = 'installed';

const setLocalStatus = (status) => {
  localStorage.setItem(INSTALL_STORAGE_KEY, status);
  return status;
};

export const markAsInstalled = () => {
  setLocalStatus(INSTALLED_STATUS);
  window.dispatchEvent(new Event('pwa-install-status-change'));
};

// Récupère le prompt capturé dans index.html (avant le chargement de React)
// ou null si l'événement n'a pas encore été déclenché.
const getStoredPrompt = () => window.__pwaInstallPrompt || null;

// Tente de sortir d'une WebView in-app Android en rouvrant la page dans
// Chrome via un intent Android.
export const NO_CHROME_PARAM = 'nochrome';

export const escapeWebView = (path, onFail) => {
  const { host, protocol, origin } = window.location;
  const scheme = protocol.replace(':', '');
  // Sans browser_fallback_url, un navigateur Chromium renvoie vers la fiche
  // Play Store de Chrome quand celui-ci est absent. On prefere revenir sur
  // nos instructions, comme le font les navigateurs non Chromium.
  const fallback = encodeURIComponent(
    `${origin}${path}${path.includes('?') ? '&' : '?'}${NO_CHROME_PARAM}=1`
  );

  setTimeout(() => {
    if (!document.hidden) {
      onFail();
    }
  }, 1500);

  window.location.href = `intent://${host}${path}`
    + `#Intent;scheme=${scheme};package=com.android.chrome;`
    + `S.browser_fallback_url=${fallback};end`;
};

const useInstallPwa = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(getStoredPrompt);
  const [isAppInstalled, setIsAppInstalled] = useState(
    () => localStorage.getItem(INSTALL_STORAGE_KEY)
  );

  const updateStatus = (status) => {
    setIsAppInstalled(setLocalStatus(status));
  };

  useEffect(() => {
    setDeferredPrompt((current) => current || getStoredPrompt());
    const onPrompt = () => setDeferredPrompt(getStoredPrompt());
    window.addEventListener('pwa-prompt-ready', onPrompt);

    const onInstalled = () => {
      window.__pwaInstallPrompt = null;
      setDeferredPrompt(null);
      updateStatus(INSTALLED_STATUS);
    };
    window.addEventListener('appinstalled', onInstalled);

    const onStatusChange = () => {
      setIsAppInstalled(localStorage.getItem(INSTALL_STORAGE_KEY));
    };
    window.addEventListener('pwa-install-status-change', onStatusChange);

    return () => {
      window.removeEventListener('pwa-prompt-ready', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener(
        'pwa-install-status-change', onStatusChange
      );
    };
  }, []);

  const canPromptNatively = Boolean(deferredPrompt);

  // Retourne true si installé, false si refusé par l'utilisateur,
  // null si le prompt natif est indisponible ou a échoué
  const triggerInstall = async () => {
    if (!deferredPrompt) {
      return null;
    }
    const prompt = deferredPrompt;
    window.__pwaInstallPrompt = null;
    setDeferredPrompt(null);

    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        updateStatus(INSTALLED_STATUS);
      }
      return outcome === 'accepted';
    } catch {
      return null;
    }
  };

  return {
    isAppInstalled,
    triggerInstall,
    canPromptNatively,
  };
};

export default useInstallPwa;
